import { useState } from 'react';

interface PlataformaConfigProps {
  configs: Record<string, { valor: string; descricao: string; tipo: string; categoria: string }>;
  onUpdate: (chave: string, valor: string) => void;
}

type CategoriaConfig = 'taxas' | 'limites' | 'regras' | 'geral';

const categorias: { id: CategoriaConfig; label: string; icon: string; descricao: string }[] = [
  { id: 'taxas', label: 'Taxas', icon: 'ri-percent-line', descricao: 'Taxas de processamento, compra e transferência' },
  { id: 'limites', label: 'Limites', icon: 'ri-ruler-line', descricao: 'Limites de resgate, transferência e pontos' },
  { id: 'regras', label: 'Regras', icon: 'ri-file-list-3-line', descricao: 'Pontuação, bonificação, prazos e verificações' },
  { id: 'geral', label: 'Geral', icon: 'ri-settings-3-line', descricao: 'Email, moeda e moderação' },
];

function getValorDisplay(valor: string, tipo: string) {
  if (tipo === 'boolean') {
    return valor === 'true' ? (
      <span className="inline-flex items-center space-x-1 text-emerald-600">
        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-sm"></i></div>
        <span className="text-xs font-bold">Ativo</span>
      </span>
    ) : (
      <span className="inline-flex items-center space-x-1 text-gray-400">
        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line text-sm"></i></div>
        <span className="text-xs font-bold">Inativo</span>
      </span>
    );
  }
  if (tipo === 'porcentagem') return <span className="text-sm font-bold text-gray-800">{valor}%</span>;
  if (tipo === 'numero') return <span className="text-sm font-bold text-gray-800">{Number(valor).toLocaleString('pt-BR')}</span>;
  return <span className="text-sm text-gray-800">{valor}</span>;
}

function getTipoInput(tipo: string) {
  switch (tipo) {
    case 'boolean': return 'checkbox';
    case 'numero':
    case 'porcentagem': return 'number';
    default: return 'text';
  }
}

export default function PlataformaConfig({ configs, onUpdate }: PlataformaConfigProps) {
  const [activeCategoria, setActiveCategoria] = useState<CategoriaConfig>('taxas');
  const [editando, setEditando] = useState<string | null>(null);
  const [editValor, setEditValor] = useState('');

  const configsFiltradas = Object.entries(configs).filter(([, c]) => c.categoria === activeCategoria);

  const handleEdit = (chave: string, valor: string) => {
    setEditando(chave);
    setEditValor(valor);
  };

  const handleSave = (chave: string) => {
    onUpdate(chave, editValor);
    setEditando(null);
  };

  const handleCancel = () => {
    setEditando(null);
    setEditValor('');
  };

  const categoriaAtual = categorias.find((c) => c.id === activeCategoria);

  return (
    <div className="space-y-5">
      {/* Categorias */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {categorias.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategoria(cat.id)}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
              activeCategoria === cat.id ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-100 hover:border-gray-200'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${activeCategoria === cat.id ? 'bg-emerald-100' : 'bg-gray-50'}`}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${cat.icon} ${activeCategoria === cat.id ? 'text-emerald-600' : 'text-gray-400'} text-lg`}></i>
              </div>
            </div>
            <div>
              <p className={`text-sm font-bold ${activeCategoria === cat.id ? 'text-emerald-700' : 'text-gray-700'}`}>{cat.label}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {Object.values(configs).filter((c) => c.categoria === cat.id).length} configurações
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${categoriaAtual?.icon} text-emerald-600 text-sm`}></i>
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-800">{categoriaAtual?.label}</h2>
              <p className="text-xs text-gray-400">{categoriaAtual?.descricao}</p>
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          {configsFiltradas.map(([chave, config]) => (
            <div key={chave} className="px-5 py-4 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-bold text-gray-800">{config.descricao}</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-bold uppercase">{config.tipo}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1 font-mono">{chave}</p>
              </div>
              <div className="flex items-center space-x-3 flex-shrink-0">
                {editando === chave ? (
                  <div className="flex items-center space-x-2">
                    {config.tipo === 'boolean' ? (
                      <input
                        type="checkbox"
                        checked={editValor === 'true'}
                        onChange={(e) => setEditValor(e.target.checked ? 'true' : 'false')}
                        className="w-5 h-5 rounded border-gray-300 text-emerald-600"
                      />
                    ) : (
                      <input
                        type={getTipoInput(config.tipo)}
                        value={editValor}
                        onChange={(e) => setEditValor(e.target.value)}
                        className="w-32 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        step={config.tipo === 'porcentagem' || config.tipo === 'numero' ? '0.01' : undefined}
                      />
                    )}
                    <button onClick={() => handleSave(chave)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-sm"></i></div>
                    </button>
                    <button onClick={handleCancel} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line text-sm"></i></div>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <div className="text-right">{getValorDisplay(config.valor, config.tipo)}</div>
                    <button onClick={() => handleEdit(chave, config.valor)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-pencil-line text-sm"></i></div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {configsFiltradas.length === 0 && (
            <div className="px-5 py-12 text-center text-gray-400 text-sm">Nenhuma configuração nesta categoria.</div>
          )}
        </div>
      </div>
    </div>
  );
}